// Schedule Page - Weekly anime airing schedule
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Stack, Card, CardContent, Avatar, Skeleton } from '@mui/material';
import { motion } from 'framer-motion';
import { CalendarMonth, LiveTv } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { fetchLatest } from '../../hooks/useApi';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function Schedule() {
  const navigate = useNavigate();
  const [selectedDay, setSelectedDay] = React.useState(new Date().getDay);
  const today = new Date().getDay();

  const { data: allAnime = [], isLoading } = useQuery({
    queryKey: ['schedule'],
    queryFn: () => fetchLatest(1, 100),
    staleTime: 5 * 60 * 1000,
  });

  // Simulate airing schedule by distributing anime across days
  const scheduleItems = React.useMemo(() =>
    allAnime.map((anime: any, i: number) => ({
      ...anime,
      day: i % 7,
      time: `${8 + (i % 12)}:00 JST`,
    }))
  , [allAnime]);

  const filteredItems = scheduleItems.filter((s: any) => s.day === selectedDay);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={900} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonth /> Airing Schedule
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Weekly anime release schedule (simulated)
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 4 }}>
        {DAYS.map((day, index) => (
          <Chip
            key={day}
            label={index === today ? `${day} (Today)` : day}
            clickable
            variant={selectedDay === index ? 'filled' : 'outlined'}
            color={selectedDay === index ? 'primary' : 'default'}
            onClick={() => setSelectedDay(index)}
            icon={index === today ? <LiveTv /> : undefined}
          />
        ))}
      </Stack>

      {isLoading ? (
        <Stack spacing={2}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: 2 }} />
          ))}
        </Stack>
      ) : filteredItems.length > 0 ? (
        <Stack spacing={2}>
          {filteredItems.map((anime: any, index: number) => (
            <motion.div
              key={`${anime.id}-${index}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.03 }}
            >
              <Card
                onClick={() => navigate(`/anime/${anime.id}`)}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'var(--anime-token-brand-primary-main)',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <Box sx={{ minWidth: 80, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">{anime.time}</Typography>
                  </Box>
                  <Avatar
                    src={anime.poster}
                    variant="rounded"
                    sx={{ width: 64, height: 40, bgcolor: 'var(--anime-token-surface-variant)' }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700} noWrap>{anime.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {anime.type} · Episode ~{anime.episodes ? Math.ceil(anime.episodes / 2) : '?'}
                    </Typography>
                  </Box>
                  <LiveTv sx={{ color: 'var(--anime-token-brand-primary-main)', fontSize: 20 }} />
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Stack>
      ) : (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <CalendarMonth sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
          <Typography variant="h6" color="text.tertiary">No scheduled releases for {DAYS[selectedDay]}</Typography>
          <Typography variant="body2" color="text.secondary">Check back later or select another day</Typography>
        </Box>
      )}
    </Box>
  );
}